import React, { useContext, useState } from 'react';

// COMPONENTS
import Avatar from 'components/common/Avatar';

// ICONS
import { Eye, Sun, Moon, RotateCcw, Check, X } from 'lucide-react';

// UTILS
import iconMap from 'utils/iconMap';
import { getDefaultTiles, upsertUserPreferences } from 'services/tileService';
import { MapPreferencesToTiles } from 'utils/mapPreferencesToTiles';

// CONTEXT
import { ToastContext } from 'context/ToastContext';

const CardUser = ({ user, handleOpenModal }) => {
    const { addToast } = useContext(ToastContext);
    const [isResetting, setIsResetting] = useState(false);
    const [showResetConfirmation, setShowResetConfirmation] = useState(false);
    const logos = {
        Google: iconMap['google'],
        Bing: iconMap['bing'],
        QWant: iconMap['qwant'],
        Yahoo: iconMap['yahoo'],
        DuckDuckGo: iconMap['duckduckgo'],
    };

    const FRIcon = iconMap['france'];
    const ENIcon = iconMap['english'];

    const formatLastConnection = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);

            const parisFormatter = new Intl.DateTimeFormat('en-US', {
                timeZone: 'Europe/Paris',
                year: 'numeric', month: 'numeric', day: 'numeric',
                hour: 'numeric', minute: 'numeric', second: 'numeric',
                hourCycle: 'h23'
            });
            const utcFormatter = new Intl.DateTimeFormat('en-US', {
                timeZone: 'UTC',
                year: 'numeric', month: 'numeric', day: 'numeric',
                hour: 'numeric', minute: 'numeric', second: 'numeric',
                hourCycle: 'h23'
            });

            const parisParts = parisFormatter.formatToParts(date).reduce((acc, part) => {
                if (part.type !== 'literal') acc[part.type] = parseInt(part.value);
                return acc;
            }, {});
            const utcParts = utcFormatter.formatToParts(date).reduce((acc, part) => {
                if (part.type !== 'literal') acc[part.type] = parseInt(part.value);
                return acc;
            }, {});
            
            const dateAtParisWallTime = Date.UTC(parisParts.year, parisParts.month - 1, parisParts.day, parisParts.hour, parisParts.minute, parisParts.second);
            const dateAtUtcWallTime = Date.UTC(utcParts.year, utcParts.month - 1, utcParts.day, utcParts.hour, utcParts.minute, utcParts.second);

            const offsetMilliseconds = dateAtParisWallTime - dateAtUtcWallTime;

            const correctedTimestamp = date.getTime() - offsetMilliseconds;
            const correctedDate = new Date(correctedTimestamp);

            return correctedDate.toLocaleString('fr-FR', {
                timeZone: 'Europe/Paris',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
            });
        } catch (error) {
            console.error("Error formatting date:", error);
            return dateString;
        }
    };

    const handleResetInterface = async () => {
        try {
            setIsResetting(true);
            const preferences = await getDefaultTiles(user.USR_IS_EXTERNAL ? 'extern' : 'intern', user.USR_ID);
            const preferencesData = preferences.data;

            const mapped = await MapPreferencesToTiles(preferencesData);

            // Formater les préférences dans le bon format
            const formattedPreferences = mapped.map(pref => ({
                tile_id: pref.id,
                color_id: pref.colorId,
                position_x_desktop: pref.position_x_desktop,
                position_y_desktop: pref.position_y_desktop,
                width_desktop: pref.width_desktop,
                height_desktop: pref.height_desktop,
                position_x_mobile: pref.position_x_mobile,
                position_y_mobile: pref.position_y_mobile,
                width_mobile: pref.width_mobile,
                height_mobile: pref.height_mobile,
                max_height: pref.max_height,
                min_height: pref.min_height,
                max_width: pref.max_width,
                min_width: pref.min_width
            }));

            await upsertUserPreferences(user.USR_ID, formattedPreferences);
            addToast('Succès', 'Interface réinitialisée avec succès', null, null, 'success');
        } catch (error) {
            console.error("Erreur lors de la réinitialisation de l'interface:", error);
            addToast('Erreur', "Erreur lors de la réinitialisation de l'interface", null, null, 'error');
        } finally {
            setIsResetting(false);
            setShowResetConfirmation(false);
        }
    };

    const handleResetClick = () => {
        setShowResetConfirmation(true);
    };

    const handleCancelReset = () => {
        setShowResetConfirmation(false);
    };

    return (
        <div
            key={user.USR_ID}
            className="flex flex-col sm:flex-row items-start sm:items-center bg-blanc dark:bg-dark-secondary rounded-lg p-4 shadow-dropping-light dark:shadow-dropping-dark hover:shadow-hover-light dark:hover:shadow-hover-dark transition-shadow"
        >
            <div className="flex-shrink-0 size-12 mb-4 sm:mb-0">
                <Avatar email={user.USR_EMAILADDRESS} alt="Avatar" className="size-12" rounded={true} />
            </div>
            {/* Informations utilisateur */}
            <div className="ml-0 sm:ml-4 flex-1 w-full sm:w-auto">
                <p className="font-semibold text-lg">{user.USR_EMAILADDRESS}</p>
                <p className="text-sm text-gris flex items-center">
                    {user.USR_AGENCY}
                    {user.USR_LANGUAGE === 'FR' ? (
                        <FRIcon className="size-4 ml-4" />
                    ) : (
                        <ENIcon className="size-4 ml-4" />
                    )}
                </p>
                <div className="flex items-center mt-1">
                    <img
                        src={logos[user.USR_BROWSER]}
                        alt={user.USR_BROWSER}
                        className="size-5 mr-2"
                    />
                    <p className="text-sm text-gris">{user.USR_BROWSER}</p>
                </div>
                <p className="text-sm text-gris">
                    Dernière connexion : {formatLastConnection(user.USR_LAST_CONNECTION_AT)}
                </p>
            </div>
            <div className="flex items-center space-x-4 mt-4 sm:mt-0">
                {/* Mode sombre pastille */}
                {user.USR_DARK_MODE ? (
                    <Moon className="size-6 text-dark dark:text-blanc" />
                ) : (
                    <Sun className="size-6 text-dark dark:text-blanc" />
                )}
                {/* Admin badge */}
                {user.USR_IS_ADMIN && (
                    <span className="text-xs bg-bleu text-blanc px-2 py-1 rounded-full">
                        Admin
                    </span>
                )}
                {/* Externe badge */}
                {user.USR_IS_EXTERNAL ? (
                    <span className="text-xs bg-emeraude text-blanc px-2 py-1 rounded-full">
                        Externe
                    </span>
                ) : (
                    <span className="text-xs bg-framboise text-blanc px-2 py-1 rounded-full">
                        Interne
                    </span>
                )}
            </div>
            <div className="ml-0 sm:ml-5 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 mt-4 sm:mt-0 w-full sm:w-auto" >
                {/* Bouton Voir l'interface */}
                <div
                    onClick={() => handleOpenModal(user)}
                    className="flex group items-center justify-center text-sm cursor-pointer bg-bleu px-4 py-2 rounded-md hover:bg-emeraude transition-all ease-in-out duration-300"
                >
                    <Eye className="size-5 text-blanc group-hover:mr-2 transition-all duration-300 ease-in-out" />
                    <div className="inline-block overflow-hidden max-w-0 group-hover:max-w-xs transition-all duration-500 ease-in-out text-blanc whitespace-nowrap">
                        Voir l'interface
                    </div>
                </div>
                {/* Bouton Réinitialiser l'interface */}
                {showResetConfirmation ? (
                    <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                        <div
                            onClick={handleResetInterface}
                            disabled={isResetting}
                            className="flex group items-center justify-center text-sm cursor-pointer bg-rouge px-4 py-2 rounded-md hover:bg-rouge-fonce transition-all ease-in-out duration-300"
                        >
                            <Check className="size-5 text-blanc group-hover:mr-2 transition-all duration-300 ease-in-out" />
                            <div className="inline-block overflow-hidden max-w-0 group-hover:max-w-xs transition-all duration-500 ease-in-out text-blanc whitespace-nowrap">
                                Confirmer
                            </div>
                        </div>
                        <div
                            onClick={handleCancelReset}
                            className="flex group items-center justify-center text-sm cursor-pointer bg-gris px-4 py-2 rounded-md hover:bg-gris-fonce transition-all ease-in-out duration-300"
                        >
                            <X className="size-5 text-blanc group-hover:mr-2 transition-all duration-300 ease-in-out" />
                            <div className="inline-block overflow-hidden max-w-0 group-hover:max-w-xs transition-all duration-500 ease-in-out text-blanc whitespace-nowrap">
                                Annuler
                            </div>
                        </div>
                    </div>
                ) : (
                    <div
                        onClick={handleResetClick}
                        className="flex group items-center justify-center text-sm cursor-pointer bg-rouge px-4 py-2 rounded-md hover:bg-rouge-fonce transition-all ease-in-out duration-300"
                    >
                        <RotateCcw className={`size-5 text-blanc group-hover:mr-2 transition-all duration-300 ease-in-out ${isResetting ? 'animate-spin' : ''}`} />
                        <div className="inline-block overflow-hidden max-w-0 group-hover:max-w-xs transition-all duration-500 ease-in-out text-blanc whitespace-nowrap">
                            Réinitialiser l'interface
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CardUser; 